import { connect, FieldArray, FormikValues } from 'formik'
import { get, omit } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { DragDropContext, Draggable, DraggableProvided, DraggableStateSnapshot, Droppable } from 'react-beautiful-dnd'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { v4 } from 'uuid'

import DropDownButton from '@/components/buttons/DropDownButton'
import DropDownItem from '@/components/buttons/DropDownItem'
import { Delete, Plus, TriangleDown2 } from '@/components/Icons'
import DraggableIcon from '@/components/Icons/Draggable'
import DraggableSelected from '@/components/Icons/DraggableSelected'
import { getAppBuilderFeatureData } from '@/pages/SpaceAppBuilder/FeaturesPage/selectors'
import {
  ActionBlockDataType,
  ActionTypes,
  FlowElement,
  RelatedFeature,
  Variable
} from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { useAppSelector } from '@/store/hooks'

import ButtonBlock from './ButtonBlock'
import EditorBlocks from './EditorBlocks'
import messages from './messages'
import { BlockBorder, BlockTitle, StyledBlockTitleBar } from './StyledComponents'

const StyledBlockBorder = styled(BlockBorder)`
  .form__field {
    margin: 0.5rem 0 1rem;
  }
`

const NotificationDetailContainer = styled.div`
  .dropdown-menu {
    margin-top: -10px;
  }
`
const StyledDropDownButton = styled(DropDownButton)`
  && {
    margin-left: 0;
    margin-bottom: 10px;
    margin-top: 10px;

    .plus-icon {
      margin: 0 1rem;
    }
  }
`

interface NotificationDetailEditorProps {
  variables: Variable[][]
  selectedElement: FlowElement
  readOnly?: boolean
}

interface DetailBlockDataType {
  blockId: string
  type: string
  text?: string
  color?: string
  horizontalAlignment?: string
  size?: string
  id?: string
  isSubtle?: boolean
  spacing?: string
  weight?: string
  wrap?: boolean
  facts?: object[]
  label?: string
  idRequired?: boolean
  value?: string
}

export interface AnyContentBlockTypes {
  displayName?: string
  initialData?: object
  Component?: any
}

interface DetailBlockType {
  [Block: string]: AnyContentBlockTypes
}

const reorder = (list: any[], startIndex: number, endIndex: number) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)

  return result
}

const NotificationDetail = ({
  formik,
  variables,
  selectedElement,
  readOnly = false
}: NotificationDetailEditorProps & FormikValues) => {
  const intl = useIntl()

  const [detailBlockTypes, setDetailBlockTypes] = useState<DetailBlockType>({})

  const featureFetchState = useAppSelector(getAppBuilderFeatureData)
  const hasStepActionFeature = useCallback(
    () =>
      featureFetchState.feature?.relatedFeatures?.some(
        (relatedFeature: RelatedFeature) => relatedFeature.stepId === selectedElement.id
      ),
    [featureFetchState.feature?.relatedFeatures, selectedElement?.id]
  )
  const actions = formik.values.stepData.notifications[0]?.actions || []

  useEffect(() => {
    if (
      actions.some((action: ActionBlockDataType) => action.type === ActionTypes.ActionSubmit) &&
      !hasStepActionFeature()
    ) {
      formik.setFieldValue('stepData.notifications[0].shouldHaveActionFeature', true)
    } else {
      formik.setFieldValue('stepData.notifications[0].shouldHaveActionFeature', false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasStepActionFeature, actions])

  useEffect(() => {
    const actions = get(formik.values, 'stepData.notifications[0].actions', [])
    if (actions.length === 0) {
      setDetailBlockTypes(omit(EditorBlocks, ['Input.Text']))
    } else if (actions.length > 0) {
      setDetailBlockTypes(omit(EditorBlocks, ['ButtonBlock']))
    } else {
      setDetailBlockTypes(EditorBlocks)
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [actions])

  const addBlock = (displayName: string | number, push: (obj: any) => void) => {
    const blockId = v4()
    const block = detailBlockTypes[displayName]
    if (displayName === 'ButtonBlock') {
      const newActionBlock = { ...ButtonBlock.initialData, blockId }
      formik.setFieldValue('stepData.notifications[0].actions', [...actions, { ...newActionBlock, type: undefined }])
    } else {
      const newBlock = { ...block.initialData, blockId }
      push(newBlock)
    }
  }

  const handleOnDragEnd = (result: any) => {
    // dropped outside the list
    if (!result.destination) {
      return
    }

    const newDetail = reorder(
      formik.values.stepData.notifications[0].detail,
      result.source.index,
      result.destination.index
    )
    formik.setFieldValue('stepData.notifications[0].detail', newDetail)
  }

  const renderDraggableBlock = (
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot,
    index: number,
    arrayHelpers: any
  ) => {
    const block = formik.values.stepData.notifications[0].detail[index]
    const DetailBlock = detailBlockTypes[block.type]
    return (
      <div {...provided.draggableProps} ref={provided.innerRef}>
        <StyledBlockBorder style={{ backgroundColor: '#fff', zIndex: '50' }}>
          <StyledBlockTitleBar {...provided.dragHandleProps} data-testid="draggable">
            <div className="icon draggable">{snapshot.isDragging ? <DraggableSelected /> : <DraggableIcon />}</div>
            {/* @ts-expect-error */}
            <BlockTitle {...provided.dragHandleProps}>{intl.formatMessage(messages[block.type])}</BlockTitle>
            {!readOnly && (
              <div className="icon remove">
                <Delete title="Delete Detail Block" onClick={() => arrayHelpers.remove(index)} />
              </div>
            )}
          </StyledBlockTitleBar>

          <DetailBlock.Component
            name={`stepData.notifications[0].detail[${index}]`}
            variables={variables}
            readOnly={readOnly}
          />
        </StyledBlockBorder>
      </div>
    )
  }

  return (
    <NotificationDetailContainer>
      <FieldArray
        name={`stepData.notifications[0].detail`}
        render={arrayHelpers => (
          <>
            <DragDropContext onDragEnd={handleOnDragEnd}>
              <Droppable
                droppableId="droppable"
                renderClone={(provided, snapshot, rubric) =>
                  renderDraggableBlock(provided, snapshot, rubric.source.index, arrayHelpers)
                }
              >
                {provided => (
                  <div {...provided.droppableProps} ref={provided.innerRef} data-testid="droppable">
                    {formik.values.stepData.notifications[0].detail?.map(
                      (block: DetailBlockDataType, index: number) => {
                        const DetailBlock = detailBlockTypes[block.type]
                        return (
                          DetailBlock?.Component && (
                            <Draggable key={block.blockId} draggableId={block.blockId} index={index}>
                              {(provided, snapshot) => renderDraggableBlock(provided, snapshot, index, arrayHelpers)}
                            </Draggable>
                          )
                        )
                      }
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
            {!readOnly && (
              <StyledDropDownButton
                onSelected={(value: string | number) => addBlock(value, arrayHelpers.push)}
                className="new-block-button"
                title={
                  <>
                    <Plus style={{ color: '#cccccc' }} />
                    <span className="plus-icon">{intl.formatMessage(messages.notificationDetailAddBlock)}</span>
                    <TriangleDown2 />
                  </>
                }
              >
                {Object.values(detailBlockTypes).map((block: any) => (
                  <DropDownItem key={block.displayName} value={block.displayName}>
                    {/* @ts-expect-error */}
                    <span>{intl.formatMessage(messages[block.displayName])}</span>
                  </DropDownItem>
                ))}
              </StyledDropDownButton>
            )}
          </>
        )}
      />
      {formik.values.stepData.notifications[0].actions?.length > 0 && (
        <ButtonBlock.Component
          name={`stepData.notifications[0].actions`}
          variables={variables}
          selectedElement={selectedElement}
          readOnly={readOnly}
        />
      )}
    </NotificationDetailContainer>
  )
}

export default connect(NotificationDetail)
